import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EnhancedInput } from '@/components/ui/enhanced-input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/components/AuthProvider';
import { getFirestore, collection, query, getDocs, where, limit } from 'firebase/firestore';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { AlertCircle, Mail, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { showNotification } from '@/lib/generalNotifications';
import axios from 'axios';

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitation = searchParams.get('invitation');
  const { user, loading: authLoading } = useAuth();
  
  const [invitationData, setInvitationData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user && !invitation) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate, invitation]);

  // Fetch invitation data
  useEffect(() => {
    async function fetchInvitation() {
      if (!invitation || typeof invitation !== 'string') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const db = getFirestore();
        const invitationsCollection = collection(db, 'invitations');
        const q = query(invitationsCollection, where('token', '==', invitation), limit(1));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setError('Invalid or expired invitation. Please contact your administrator.');
          setLoading(false);
          return;
        }

        const invitationDoc = snapshot.docs[0];
        const data = invitationDoc.data();

        // Check if invitation is expired
        if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
          setError('This invitation has expired. Please contact your administrator for a new invitation.');
          setLoading(false);
          return;
        }

        // Check if invitation is already used
        if (data.status === 'completed') {
          setError('This invitation has already been used. Please log in or contact your administrator.');
          setLoading(false);
          return;
        }

        setInvitationData({
          id: invitationDoc.id,
          ...data
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching invitation:', error);
        setError('Failed to retrieve invitation. Please try again or contact your administrator.');
        setLoading(false);
      }
    }

    fetchInvitation();
  }, [invitation]);

  const validatePassword = () => {
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    
    setPasswordError(null);
    return true;
  };

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePassword() || !invitationData || !invitation) return;
    
    try {
      setIsProcessing(true);
      
      // Use the backend API instead of direct Firebase calls
      const response = await axios.post('/api/signup', {
        invitation,
        password,
        signupMethod: 'email'
      });
      
      if (response.data.success) {
        showNotification({
          title: 'Account Created',
          message: 'Your account has been created successfully! You will now be redirected to the login page.',
          type: 'success'
        });
        
        // Give the user a moment to read the notification before redirecting
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              email: invitationData.email,
              message: 'Your account has been created. Please log in with your new credentials.' 
            } 
          });
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Failed to create account. Please try again.');
      }
      
    } catch (error: any) {
      console.error('Email signup error:', error);
      setError(error.response?.data?.error || error.message || 'Failed to create account. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleGoogleSignup = async () => {
    if (!invitationData || !invitation) return;
    
    try {
      setIsProcessing(true);
      
      // For Google signup, we need to authenticate with Firebase first
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if the Google account email matches the invitation email
      if (result.user.email !== invitationData.email) {
        showNotification({
          title: 'Email Mismatch',
          message: `Please sign up with the email address (${invitationData.email}) that received the invitation.`,
          type: 'error'
        });
        
        // Sign out the user since the email doesn't match
        await auth.signOut();
        setIsProcessing(false);
        return;
      }
      
      // Then use the backend API to complete the signup process
      const response = await axios.post('/api/signup', {
        invitation,
        signupMethod: 'google'
      });
      
      if (response.data.success) {
        showNotification({
          title: 'Success',
          message: response.data.message || 'Your account has been created successfully!',
          type: 'success'
        });
        
        navigate('/dashboard');
      } else {
        // Sign out the user if the backend process failed
        await auth.signOut();
        throw new Error(response.data.error || 'Failed to create account. Please try again.');
      }
      
    } catch (error: any) {
      console.error('Google signup error:', error);
      setError(error.response?.data?.error || error.message || 'Failed to create account with Google. Please try again.');
      setIsProcessing(false);
      
      // Sign out the user if there was any error
      try {
        await auth.signOut();
      } catch (signOutError) {
        console.error('Error signing out after failed Google signup:', signOutError);
      }
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-r-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Invitation Error</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="mt-6 text-center">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitationData) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">No Invitation Found</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Invalid Invitation</AlertTitle>
              <AlertDescription>
                No valid invitation was found. Please use the link from your invitation email.
              </AlertDescription>
            </Alert>
            <div className="mt-6 text-center">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">Welcome {invitationData.firstName}</CardTitle>
          <CardDescription className="text-center">
            Please create a password to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <form onSubmit={handleEmailSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <EnhancedInput
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <EnhancedInput
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                />
              </div>
              
              {passwordError && (
                <Alert variant="destructive" className="py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{passwordError}</AlertDescription>
                </Alert>
              )}
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-r-foreground" />
                    <span>Creating Account...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>Create Account with Email</span>
                  </div>
                )}
              </Button>
            </form>
            
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>
            
            <Button 
              type="button" 
              variant="outline" 
              className="w-full" 
              onClick={handleGoogleSignup}
              disabled={isProcessing}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col items-center justify-center text-center text-sm text-muted-foreground">
          <p>Already have an account?{' '}
            <a href="/login" className="text-primary hover:underline">
              Log in
            </a>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
} 